/**
 * Instruction-shape guard (SNT-1).
 *
 * Detects "instruction-shaped" sentences that AI models occasionally emit
 * when they fail to ground a section's content. Pattern: a meta-sentence
 * that DESCRIBES what the section should contain instead of containing it.
 *
 * Symptom that motivated this guard (Redhead Feasibility v4.0, 2026-05-18):
 *   "The Siting Notes should cite the FEMA National Flood Hazard Layer for
 *    the 'AE' zone designation and the KMZ layer '100y FZ' for the 880m
 *    setback distance."
 *
 * Customer reads this as "the AI is telling itself what to do" — which is
 * exactly what it is. The model echoed back the SHAPE of the instruction
 * directive embedded in the narrative prompt
 * (`reportPrompts.ts:2085` — "9. SITING NOTES: Reference elevation, slope,
 *  flood zone, ...") instead of grounding the section.
 *
 * Examples this catches:
 *   "The Siting Notes should cite the FEMA Flood Hazard Layer..."
 *   "This section must reference current zoning..."
 *   "Confirm the report addresses solar capacity..."
 *   "Reference elevation, slope, flood zone..."
 *
 * Examples it does NOT catch (these are valid findings):
 *   "The site cites the FEMA Flood Hazard Layer." — past tense, no modal
 *   "Current zoning is M-1 [Ref 12]."             — declarative
 *   "Solar capacity is 4.53 kWh/m²/day [Ref 43]." — declarative
 *
 * Composes with:
 *   - VMR-2 — retry path's second-pass output flows through this guard.
 *   - RDX   — sections flagged here MUST be skipped by `applyReportToSed`.
 *   - RCO   — citation counts for quarantined sections get decremented.
 *   - UAE-2 — quarantine banner's "Author" CTA reuses the authored-claim flow.
 *
 * The guard runs at GEN time (siteRagService consume site), not at READ time.
 * Old reports keep their original content; new reports get the guard.
 */

/**
 * Pattern decomposition (kept here for reviewers):
 *
 * Branch 1 — capitalized noun phrase + modal + reporting verb:
 *   "The Siting Notes should cite ..."
 *   "The Power Section must reference ..."
 *   "Site Analysis is expected to address ..."
 *
 * Branch 2 — meta self-reference:
 *   "This section should ..."
 *   "This report must ..."
 *   "This analysis needs to ..."
 *
 * Branch 3 — Confirm-the-report (DAM Key Analytical Question echo):
 *   "Confirm the report addresses ..."
 *   "Confirm that the report ..."
 *
 * Branch 4 — bare directive lifted verbatim from prompt:
 *   "Reference elevation, ..."        — exact wording at reportPrompts.ts:2085
 *   "Note any coastal proximity ..."  — exact wording at reportPrompts.ts:2085
 *
 * Branch 5 — reviewer-pipeline correctedSections meta-leaks:
 *   "Use the text provided in correctedSections.executiveSummary"
 *   "… is provided in the 'correctedSections.executiveSummary' field."
 *   Any bare `correctedSections.<field>` token (internal pipeline
 *   name — never legitimate in customer-facing prose).
 *
 * Branch 6 — executive-summary prompt echo (Environmental BTM Permitting
 * Outlook, 2026-05-24):
 *   "An executive summary should be generated, synthesizing key findings
 *    from the SED. It must highlight the site's low seismic risk …"
 *   Mirrors `reportPrompts.ts` EXECUTIVE SUMMARY — BTM PERMITTING OUTLOOK
 *   block (lines ~3283–3289): schema-field meta, synthesize directive,
 *   "It must highlight", "Lead with favourable site indicators".
 *
 * Branch 7 — QA-loop / example-echo leaks (Water Resource v7, 2026-05):
 *   "A full executive summary should be provided. Example: This report
 *    assesses the feasibility of developing a 1500 MW data center …"
 *   Model prefixes real prose with meta + copies example-shaped template.
 */
const INSTRUCTION_SHAPE_RE =
  /\b((?:A|An|The)\s+(?:full\s+)?[Ee]xecutive [Ss]ummary\s+should\s+be\s+provided|(?:An|The)\s+[Ee]xecutive [Ss]ummary\s+(should|must|needs to)|(It|This)\s+must\s+highlight|[Ss]ynthesiz(e|ing)\s+key\s+(SED\s+)?findings|[Tt]he\s+executiveSummary\s+field|[Ll]ead\s+with\s+(favourable|favorable)\s+site\s+indicators|Example:\s*This report\b|The\s+[A-Z][A-Za-z ]{2,40}\s+(should|must|needs to|is expected to|is required to)\s+(cite|reference|address|cover|note|include|mention|describe|discuss|explain|consider)|(?:This|The|A|An)\s+(?:full\s+|brief\s+|draft\s+|final\s+|short\s+)?(?:summary|section|analysis|narrative|prose|report|conclusion|overview|paragraph|sentence|wording|draft|write-?up|text|abstract)\s+(?:should|must|needs to|ought to|could)\s+(?:be\s+|been\s+)?(?:revised|rewritten|reworded|reworked|expanded|condensed|shortened|strengthened|improved|clarified|tightened|fleshed\s+out|made\s+more|updated\s+to|reframed|polished)|This\s+(section|report|analysis|narrative)\s+(should|must|needs to)|Confirm\s+(the\s+report|that\s+the\s+report)|Reference\s+(elevation|flood|zoning|coastal)|Note\s+any\s+(coastal|elevation)|Use\s+the\s+text\s+provided\s+in\s+correctedSections\.\w+|provided\s+in\s+the\s+['"][^'"]*correctedSections\.\w+[^'"]*['"]\s+field|correctedSections\.\w+)/i;

export interface InstructionShapeReport {
  /** True iff the text reads as an instruction rather than a finding. */
  flagged: boolean;
  /** The substring that triggered the match (kept short for telemetry). */
  matchedPattern?: string;
  /** ~140-char window centred on the match for human review. */
  snippet?: string;
}

/**
 * Detect instruction-shape language in arbitrary AI-generated text.
 *
 * Returns `{ flagged: false }` for empty / null / undefined input — never
 * throws. The caller is expected to swap the text for a data-grounded
 * fallback when `flagged === true`.
 *
 * Precision target: >95%. The regex is tightly scoped to capitalized
 * noun phrase + modal + reporting verb (or the bare directive wordings
 * the prompt actually uses). False-positives degrade gracefully because
 * the visual quarantine banner is recoverable — the user can override
 * with an Authored claim or re-run the section.
 */
export function detectInstructionShape(text: string | undefined | null): InstructionShapeReport {
  if (!text || typeof text !== 'string') return { flagged: false };
  const m = text.match(INSTRUCTION_SHAPE_RE);
  if (!m || m.index == null) return { flagged: false };
  return {
    flagged: true,
    matchedPattern: m[0],
    snippet: text.slice(Math.max(0, m.index - 20), Math.min(text.length, m.index + 120)),
  };
}
